import { useQuery } from '@tanstack/react-query';
import { EMPTY_COLUMN_VALUE } from 'constants/Common';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import React, { useCallback, useEffect } from 'react';
import { Formatter } from 'utils';
import * as S from './styles';

interface Props {
  orderId: string | number;
}

interface Detail {
  label: string;
  value: string | number | null;
}

export const OrderPrinting: React.FC<Props> = ({ orderId }) => {
  const { getOrderPrint } = useOrder();

  const onFetchSuccess = useCallback(() => {
    setTimeout(() => {
      window.print();
    }, 500);
  }, []);

  const onFetchFailure = useCallback(() => {
    setTimeout(() => {
      window.close();
    }, 3000);
  }, []);

  const {
    isPending: loading,
    isSuccess,
    isError,
    data: orders,
  } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderPrint(orderId),
  });

  if (isSuccess) {
    onFetchSuccess();
  }

  if (isError) {
    onFetchFailure();
  }

  const Header = useCallback(({ order }: any): JSX.Element => {
    if (!order) return <></>;

    // const logoUrl = order.company.logoUrl;
    const logoUrl = '';

    const type =
      order?.orderType.name === 'Carregamento'
        ? 'carregamento'
        : order?.orderType.name === 'Descarregamento'
          ? 'descarregamento'
          : '...';
    // const orderNumber = `${order.id}`.padStart(4, '0');
    const date = Formatter.date(order.scheduledAt, {
      format: 'dd/MM/yyyy HH:mm',
    });

    return (
      <S.PageHeader>
        {!!logoUrl && (
          <S.CompanyLogo>
            <img src={logoUrl} alt="" />
          </S.CompanyLogo>
        )}
        <S.PageHeaderTitle>
          <h4>Solicitação de {type}</h4>
          <span>{date}</span>
        </S.PageHeaderTitle>
        <S.PageHeaderStatus>
          <span>Status: {Formatter.orderStatusName(order.status)}</span>
          {!!order.cancelationReason && (
            <span>
              Justificativa de cancelamento: {order.cancelationReason.name}
            </span>
          )}
        </S.PageHeaderStatus>
      </S.PageHeader>
    );
  }, []);

  const WarehouseGrid = useCallback(({ order }: any): JSX.Element => {
    if (!order) return <></>;
    const { warehouse, dock } = order;

    return (
      <S.WarehouseGrid>
        <S.Detail>
          <S.DetailLabel>Armazém</S.DetailLabel>
          <S.DetailValue>{warehouse.tradeName}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Doca</S.DetailLabel>
          <S.DetailValue>{dock.name}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Volume Total</S.DetailLabel>
          <S.DetailValue>
            {order.totalVolume.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
            })}
          </S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Endereço do armazém</S.DetailLabel>
          <S.DetailValue>{Formatter.address(warehouse)}</S.DetailValue>
        </S.Detail>
      </S.WarehouseGrid>
    );
  }, []);

  const MainGrid = useCallback(({ order }: any): JSX.Element => {
    if (!order) return <></>;

    const {
      carrier,
      operationType,
      vehicleType,
      driverName,
      driverDocument,
      vehiclePlate,
      cargoType,
      devolutionNumber,
      exportationSecuritySeal,
      exportationContainer,
      importationOrder,
      importationSequential,
      importationContainer,
      importationDeclaration,
      totalVolume,
    } = order;

    const getNullableDetail = (
      label: string,
      value: string | number | null
    ): Detail[] => {
      if (value === null) return [];
      return [{ label, value }];
    };

    const details: Detail[] = [
      // {
      //   label: 'Cliente',
      //   value: company?.tradeName,
      // },
      // {
      //   label: 'Inscrição Estadual',
      //   value: company.ie || '---',
      // },
      // {
      //   label: 'Fazenda',
      //   value: company.addressComplement || '---',
      // },
      // {
      //   label: 'Qtd itens',
      //   value: orderItems.length,
      // },
      // {
      //   label: 'Volume',
      //   value: totalVolume.toLocaleString('pt-BR', {
      //     minimumFractionDigits: 2,
      //   }),
      // },
      // {
      //   label: 'Peso',
      //   value: Formatter.weight(totalWeight),
      // },
      {
        label: 'Transportadora',
        value: carrier.tradeName,
      },
      // {
      //   label: 'Tipo de ordem',
      //   value: orderType.name,
      // },
      {
        label: 'Motorista',
        value: driverName,
      },
      {
        label: 'CPF do motorista',
        value: Formatter.document(driverDocument),
      },
      {
        label: 'Tipo de veículo',
        value: vehicleType.name,
      },
      {
        label: 'Placa do veículo',
        value: vehiclePlate,
      },
      {
        label: 'Tipo de carga',
        value: cargoType.name,
      },
      {
        label: 'Tipo de operação',
        value: operationType.name,
      },
      ...getNullableDetail('Nº ordem devolução', devolutionNumber),
      ...getNullableDetail('Container', exportationContainer),
      ...getNullableDetail('Lacre', exportationSecuritySeal),
      ...getNullableDetail('Declaração', importationDeclaration),
      ...getNullableDetail('Pedido', importationOrder),
      ...getNullableDetail('Sequencial', importationSequential),
      ...getNullableDetail('Container', importationContainer),
    ];

    return (
      <S.MainGrid>
        {details.map(({ label, value }, i) => (
          <S.Detail key={i}>
            <S.DetailLabel>{label}</S.DetailLabel>
            <S.DetailValue>{value}</S.DetailValue>
          </S.Detail>
        ))}
      </S.MainGrid>
    );
  }, []);

  const InfoGrid = useCallback(({ order }: any): JSX.Element => {
    if (!order) return <></>;
    const { observation, material } = order;

    return (
      <S.InfoGrid>
        <S.Detail>
          <S.DetailLabel>Observações</S.DetailLabel>
          <S.DetailValue>{observation || EMPTY_COLUMN_VALUE}</S.DetailValue>
        </S.Detail>
        {/* <S.Detail>
          <S.DetailLabel>Materiais</S.DetailLabel>
          <S.DetailValue>{material || EMPTY_COLUMN_VALUE}</S.DetailValue>
        </S.Detail> */}
      </S.InfoGrid>
    );
  }, []);

  const Events = useCallback(({ order }: any): JSX.Element => {
    if (!order) return <></>;
    const { vehicleArrivedAt, startedAt, finishedAt, vehicleDepartedAt } =
      order;

    const getDatetimeValue = (event: string | null): string => {
      if (!event) {
        return Formatter.date(order.scheduledAt, {
          format: 'dd MMM yyyy, __:__',
        });
      }

      return Formatter.date(event, { format: 'dd MMM yyyy, HH:mm' });
    };

    return (
      <S.EventsGrid>
        <S.Detail>
          <S.DetailLabel>Chegada do veículo</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(vehicleArrivedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Início da coleta</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(startedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Fim da coleta</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(finishedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Saída do veículo</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(vehicleDepartedAt)}</S.DetailValue>
        </S.Detail>
      </S.EventsGrid>
    );
  }, []);

  const OrderItems = useCallback(({ order }: any): JSX.Element => {
    if (!order?.orderItems.length) return <></>;

    return (
      <>
        {order.orderItems.map((item: any, i: number) => (
          <S.ListItem key={item.id}>
            <S.Column>{item.code}</S.Column>
            <S.Column>{item.description.split('-')[0]}</S.Column>
            <S.Column>
              {item.description.split('-')[1]} -{' '}
              {item.description.split('-')[2]}
            </S.Column>
            <S.Column>
              {item.quantity.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}
            </S.Column>
            <S.Column>
              {item.volume.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}
            </S.Column>
            <S.Column>{item.unit}</S.Column>
            <S.Column>{item.batch}</S.Column>
            <S.Column>{item.deletedAt ? 'CANCELADO' : 'ATIVO'}</S.Column>
            <S.Column>{item.reason ? item.reason : '---'}</S.Column>
            <S.Column>-</S.Column>
          </S.ListItem>
        ))}
      </>
    );
  }, []);

  return (
    <>
      {loading && <S.ActivityIndicator />}
      {!loading && orders.length && (
        <S.PageContainer>
          <>
            <Header order={orders[0]} />
            <WarehouseGrid order={orders[0]} />
            <MainGrid order={orders[0]} />
            <InfoGrid order={orders[0]} />
            <Events order={orders[0]} />
            {/* <S.Subtitle>Itens da solicitação</S.Subtitle> */}
            <S.OrderItemsGrid>
              <S.ListHeader>
                <div>Código</div>
                <div>Descrição</div>
                <div>Cliente</div>
                <div>Qtd</div>
                <div>Vol</div>
                <div>Un</div>
                <div>Lote</div>
                <div>Status</div>
                <div>Motivo</div>
                <div>Validade</div>
              </S.ListHeader>
              {orders.map((order: any) => {
                return (
                  <>
                    <OrderItems key={order.id} order={order} />
                  </>
                );
              })}
            </S.OrderItemsGrid>
          </>
          {/* <OrderItems order={orders[0]} /> */}
        </S.PageContainer>
      )}
    </>
  );
};
